import { useAtom, useSetAtom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import { useCallback } from 'react';

/** What the reader was doing when the gate stopped them. */
export type SaveGateAction = 'save' | 'follow';

/** The board picked in the gate's choose-board step. */
export interface SaveGateBoardChoice {
    id: number;
    title: string;
    urlSegment?: string;
    projectUrlSegment?: string;
    isPersonal?: boolean;
    /** Created inside the picker rather than picked from the list. */
    isNew?: boolean;
}

export interface SaveGateIntent {
    action: SaveGateAction;
    /** The pin being saved. Absent for a follow. */
    pinId?: number;
    pinType?: string;
    libraryItemId?: number;
    /** Bulk save: every selected pin, so the whole selection resumes. */
    items?: { id: number; libraryItemId?: number }[];
    /** The professional being followed. Absent for a save. */
    professionalId?: number;
    /** Title for the success screen. */
    title?: string;
    /**
     * The item being saved — tile image or item hero. Shown in the gate's brand
     * pane and again on the success screen, so the reader sees the thing they
     * asked to keep rather than stock imagery.
     */
    image?: string;
    /**
     * Where the save should land, chosen in the gate's board picker. Absent
     * until that step completes, and never persisted — the picker runs after
     * auth, so no round-trip can interrupt between choosing and resuming.
     */
    board?: SaveGateBoardChoice;
}

/**
 * What the reader was trying to keep before the gate stopped them, persisted so
 * it survives a social round-trip's full reload. Session-scoped: an abandoned
 * intent must not follow them into a later visit and save something they have
 * forgotten asking for.
 */
export const SAVE_GATE_INTENT_KEY = 'save-gate-intent';
export const SAVE_GATE_OPEN_KEY = 'save-gate-open';

export const saveGateIntentAtom = atomWithStorage<SaveGateIntent | null>(
    SAVE_GATE_INTENT_KEY,
    null,
    createJSONStorage<SaveGateIntent | null>(() => sessionStorage),
);

export const saveGateOpenAtom = atomWithStorage<boolean>(
    SAVE_GATE_OPEN_KEY,
    false,
    createJSONStorage<boolean>(() => sessionStorage),
);

export interface UseSaveGate {
    intent: SaveGateIntent | null;
    isOpen: boolean;
    open: (intent: SaveGateIntent) => void;
    close: () => void;
}

/**
 * Setter-only way to open the gate, for save affordances that render in bulk
 * (every tile on a browse grid). Unlike `useSaveGate` it subscribes to
 * nothing, so opening or closing the gate re-renders no tiles.
 */
export const useOpenSaveGate = (): ((intent: SaveGateIntent) => void) => {
    const setIntent = useSetAtom(saveGateIntentAtom);
    const setIsOpen = useSetAtom(saveGateOpenAtom);

    return useCallback(
        (next: SaveGateIntent) => {
            setIntent(next);
            setIsOpen(true);
        },
        [setIntent, setIsOpen],
    );
};

export const useSaveGate = (): UseSaveGate => {
    const [intent, setIntent] = useAtom(saveGateIntentAtom);
    const [isOpen, setIsOpen] = useAtom(saveGateOpenAtom);

    const open = useCallback(
        (next: SaveGateIntent) => {
            setIntent(next);
            setIsOpen(true);
        },
        [setIntent, setIsOpen],
    );

    const close = useCallback(() => {
        setIsOpen(false);
        setIntent(null);
    }, [setIntent, setIsOpen]);

    return { intent, isOpen, open, close };
};
